import React, { Fragment, useState } from "react";
import Dashboard from "../Admin/Dashboard/Dashboard";
import CurrentDate from "../Admin/CurrentDate";
import { Link } from "react-router-dom";
import Loader from "../Admin/Dashboard/Loader";
import axios from "axios";
import baseApi from "../api";
import { useFormik } from "formik";
import * as Yup from "yup";
import "../../index.css";

const REQUIRED_SCHEMA_TYPES = ["FAQPage"];
const SCHEMA_KEY_MAP = {
  FAQPage: "faqPage"
};

const DEFAULT_SCHEMA_TEMPLATES = {
  FAQPage: {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "",
        acceptedAnswer: {
          "@type": "Answer",
          text: ""
        }
      }
    ]
  },
  Custom: {
    "@context": "https://schema.org",
    "@type": ""
  }
};

const getDefaultSchemaByType = (type) =>
  JSON.stringify(DEFAULT_SCHEMA_TEMPLATES[type] || DEFAULT_SCHEMA_TEMPLATES.Custom, null, 2);

const createWebsiteSchema = async (payload, headers) => {
  try {
    return await axios.post(`${baseApi}website-schema/`, payload, { headers });
  } catch (error) {
    if (error?.response?.status === 404) {
      return axios.post(`${baseApi}website-schema`, payload, { headers });
    }
    throw error;
  }
};

const parseSchemaValue = (schemaType, schemaText) => {
  if (!schemaText || !schemaText.trim()) {
    return null;
  }
  return JSON.parse(schemaText);
};

const buildCombinedPayload = (values) => {
  const schema = {};

  REQUIRED_SCHEMA_TYPES.forEach((schemaType) => {
    const currentItem = values.schemaItems.find((item) => item.type === schemaType);
    schema[SCHEMA_KEY_MAP[schemaType]] = currentItem
      ? parseSchemaValue(schemaType, currentItem.schema)
      : null;
  });

  const customSchema = values.schemaItems
    .filter((item) => item.type === "Custom")
    .map((item) => parseSchemaValue(item.type, item.schema))
    .filter(Boolean);

  return {
    page: values.page,
    schema,
    customSchema
  };
};

const AddWebsiteSchema = () => {
  const [isLoading, setIsloading] = useState(false);

  const initialSchemaItems = REQUIRED_SCHEMA_TYPES.map((type) => ({
    type,
    schema: getDefaultSchemaByType(type),
    isRequired: true
  }));

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      page: "global",
      isActive: true,
      schemaItems: initialSchemaItems
    },
    validationSchema: Yup.object({
      page: Yup.string(),
      schemaItems: Yup.array().of(
        Yup.object({
          type: Yup.string(),
          schema: Yup.string().test("is-json", "Schema must be valid JSON", (value) => {
            if (!value || !value.trim()) return true;
            try {
              JSON.parse(value);
              return true;
            } catch (error) {
              return false;
            }
          })
        })
      )
    }),
    onSubmit: async (values) => {
      setIsloading(true);
      try {
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwtToken")}`
        };
        const payload = buildCombinedPayload(values);
        await createWebsiteSchema(payload, headers);
        formik.resetForm();
      } catch (err) {
        console.log(err);
      } finally {
        setIsloading(false);
      }
    }
  });

  const addSchemaRow = () => {
    const nextItems = [
      ...formik.values.schemaItems,
      { type: "Custom", schema: getDefaultSchemaByType("Custom"), isRequired: false }
    ];
    formik.setFieldValue("schemaItems", nextItems);
  };

  const removeSchemaRow = (index) => {
    if (formik.values.schemaItems.length === 1) return;
    const nextItems = formik.values.schemaItems.filter((_, idx) => idx !== index);
    formik.setFieldValue("schemaItems", nextItems);
  };

  return (
    <Fragment>
      <div className="admin_panel">
        <Dashboard />

        {!isLoading ? (
          <div className="container">
            <div className="col-sm-12">
              {" "}
              <CurrentDate />
              <form onSubmit={formik.handleSubmit} encType="application/json">
                <div className="form-group">
                  <label>
                    <h3>Page/Slug</h3>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="page"
                    name="page"
                    {...formik.getFieldProps("page")}
                  />

                  <span className="text-danger">
                    {formik.touched.page &&
                    formik.errors.page ? (
                      <div>{formik.errors.page}</div>
                    ) : null}
                  </span>
                </div>

                {formik.values.schemaItems.map((item, index) => (
                  <div className="form-group" key={`schema-item-${index}`}>
                    <label>
                      <h3>{`Schema ${index + 1} Type`}</h3>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name={`schemaItems.${index}.type`}
                      value={formik.values.schemaItems[index].type}
                      readOnly
                    />

                    <span className="text-danger">
                      {formik.touched.schemaItems &&
                      formik.touched.schemaItems[index] &&
                      formik.touched.schemaItems[index].type &&
                      formik.errors.schemaItems &&
                      formik.errors.schemaItems[index] &&
                      formik.errors.schemaItems[index].type ? (
                        <div>{formik.errors.schemaItems[index].type}</div>
                      ) : null}
                    </span>

                    <label style={{ marginTop: "12px" }}>
                      <h3>{`Schema ${index + 1} JSON`}</h3>
                    </label>
                    <textarea
                      className="form-control"
                      name={`schemaItems.${index}.schema`}
                      rows="10"
                      placeholder="Enter schema JSON here..."
                      value={formik.values.schemaItems[index].schema}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />

                    <span className="text-danger">
                      {formik.touched.schemaItems &&
                      formik.touched.schemaItems[index] &&
                      formik.touched.schemaItems[index].schema &&
                      formik.errors.schemaItems &&
                      formik.errors.schemaItems[index] &&
                      formik.errors.schemaItems[index].schema ? (
                        <div>{formik.errors.schemaItems[index].schema}</div>
                      ) : null}
                    </span>

                    {!item.isRequired ? (
                      <div style={{ marginTop: "10px" }}>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeSchemaRow(index)}
                        >
                          Remove Schema
                        </button>
                      </div>
                    ) : null}
                    <hr />
                  </div>
                ))}

                <hr />

                <div className="form-group">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={addSchemaRow}
                  >
                    Add More Schema Type
                  </button>
                  &nbsp;&nbsp;
                  <input
                    type="submit"
                    className="btn btn-primary"
                    value="Add Schema"
                  />
                  &nbsp;&nbsp;
                  <Link to="/website-schema">Back to website schema</Link>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <Loader />
        )}
      </div>
    </Fragment>
  );
};

export default AddWebsiteSchema;
